import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  YES_NO_CHOICES,
  FALLS_COUNT_OPTIONS,
  MEDICATION_OPTIONS,
  NEUROLOGICAL_DISORDER_OPTIONS,
  initialFormData,
} from "./fallsRiskAssessment.config";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { createFallsRiskAssessment } from "../../../../../../api/services/assessment";
import { getCarePlans } from "../../../../../../api/services/core";
import { QK } from "../../../../../../base/qk";

const CreateFallsRiskAssessmentModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createFallsRiskAssessment(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.fallsRiskAssessments, clientID]);
        enqueueSnackbar("Falls risk assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        client: clientID,
        medications_list: data.medications_list.map((item) => item.value),
        neurological_disorders: data.neurological_disorders.map(
          (item) => item.value
        ),
      };
      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  const requiredValidation = {
    validate: (value) =>
      (value !== null && value !== undefined) || "This field is required",
  };

  const renderQuestionWithComment = (
    questionName,
    questionLabel,
    commentName,
    commentLabel
  ) => (
    <>
      <Grid item xs={12} md={6}>
        <Controller
          name={questionName}
          control={control}
          rules={requiredValidation}
          render={({ field }) => (
            <CustomSelect
              label={`${questionLabel} *`}
              options={YES_NO_CHOICES}
              {...field}
              error={!!errors[questionName]}
              helperText={errors[questionName]?.message}
              required
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name={commentName}
          control={control}
          render={({ field }) => (
            <CustomInput label={commentLabel} {...field} />
          )}
        />
      </Grid>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Falls Risk Assessment</Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      {...field}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {renderQuestionWithComment(
                "has_fallen_in_last_12_months",
                "Has service user fallen in last 12 months?",
                "fallen_how_many_times",
                "If yes, how many times?"
              )}

              {renderQuestionWithComment(
                "is_dizzy_or_lightheaded",
                "Dizzy or lightheaded when getting up?",
                "dizzy_lightheaded_comment",
                "Dizzy/Lightheaded Comment"
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="taking_more_than_4_medications"
                  control={control}
                  rules={requiredValidation}
                  render={({ field }) => (
                    <CustomSelect
                      label="Taking more than 4 medications? *"
                      options={YES_NO_CHOICES}
                      {...field}
                      error={!!errors.taking_more_than_4_medications}
                      helperText={
                        errors.taking_more_than_4_medications?.message
                      }
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="medications_list"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      placeholder="State the medication..."
                      options={MEDICATION_OPTIONS}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="medication_comment"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Medication Comment"
                      {...field}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="has_neurological_disorder"
                  control={control}
                  rules={requiredValidation}
                  render={({ field }) => (
                    <CustomSelect
                      label="Neurological Disorder? *"
                      options={YES_NO_CHOICES}
                      {...field}
                      error={!!errors.has_neurological_disorder}
                      helperText={errors.has_neurological_disorder?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="neurological_disorders"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      placeholder="Select neurological disorders..."
                      options={NEUROLOGICAL_DISORDER_OPTIONS}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="neurological_disorder_comment"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Neurological Disorder Comment"
                      {...field}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              </Grid>

              {renderQuestionWithComment(
                "is_agitated_or_confused",
                "Agitated or confused?",
                "agitated_or_confused_comment",
                "Agitated/Confused Comment"
              )}

              {renderQuestionWithComment(
                "alcohol_intake_increases_risk",
                "Alcohol intake increasing falls risk?",
                "alcohol_intake_comment",
                "Alcohol Intake Comment"
              )}

              {renderQuestionWithComment(
                "is_eyesight_impaired",
                "Eyesight impaired?",
                "eyesight_impaired_comment",
                "Eyesight Impaired Comment"
              )}

              {renderQuestionWithComment(
                "has_incontinence_or_urgency",
                "Incontinence or urgency to go to toilet?",
                "incontinence_or_urgency_comment",
                "Incontinence/Urgency Comment"
              )}

              {renderQuestionWithComment(
                "has_trips_or_hazards_at_home",
                "Trips/hazards around home?",
                "trips_or_hazards_comment",
                "Trips/Hazards Comment"
              )}

              {renderQuestionWithComment(
                "has_difficulty_getting_up",
                "Difficulty getting up from bed/chair?",
                "difficulty_getting_up_comment",
                "Difficulty Getting Up Comment"
              )}

              {renderQuestionWithComment(
                "has_previous_fractures_or_osteoporosis",
                "Previous fractures/osteoporosis?",
                "previous_fractures_comment",
                "Previous Fractures/Osteoporosis Comment"
              )}

              {renderQuestionWithComment(
                "has_worries_about_falling",
                "Does the service user have any worries or concerns about risk of falling?",
                "worries_about_falling_comment",
                "Worries About Falling Comment"
              )}

              {renderQuestionWithComment(
                "any_other_comments",
                "Any other comments?",
                "other_comments_text",
                "Other Comments"
              )}
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFallsRiskAssessmentModal;
